import { JSX, Suspense, SVGProps } from 'react';
import { twMerge } from 'tailwind-merge';
import { IconSize } from '../../types/icon';
import { omitFields } from '../../utils/object';
import svgIconMap from '../icons';

export type SvgIconName = keyof typeof svgIconMap;

type SvgIconBaseProps = {
    size?: IconSize;
} & SVGProps<SVGSVGElement>;

type SvgIconWithName = SvgIconBaseProps & {
    name: SvgIconName;
    icon?: never;
};

type SvgIconWithComponent = SvgIconBaseProps & {
    name?: never;
    icon: (props: SVGProps<SVGSVGElement>) => JSX.Element;
};

export type SvgIconProps = SvgIconWithName | SvgIconWithComponent;

function isSvgIconWithComponent(props: SvgIconProps): props is SvgIconWithComponent {
    return 'icon' in props;
}

/**
 * SvgIcon component that renders an SVG icon.
 *
 * This component can render an SVG icon either by specifying the name of the icon
 * from a predefined map (`svgIconMap`) or by providing a custom icon component.
 *
 * Note: The `name` and `icon` props are mutually exclusive. You must provide either
 * the `name` prop or the `icon` prop, but not both.
 *
 * Use the `name` prop when the icon should be dynamic, such as when it is coming from a CMS.
 * Use the `icon` prop for static cases where the icon is predefined.
 *
 * @example
 * // Render an icon by name
 * <SvgIcon name="close" size={32} onClick={handleClick} />
 *
 * @example
 * // Render a custom icon component
 * const CustomIcon = (props) => <svg {...props}>...</svg>;
 * <SvgIcon icon={CustomIcon} size={32} onClick={handleClick} />
 */
export default function SvgIcon(props: SvgIconProps): JSX.Element {
    const defaultSize: IconSize = 64;
    const size = props.size ?? defaultSize;
    const IconComponent = isSvgIconWithComponent(props) ? props.icon : svgIconMap[props.name];
    const iconClasses = twMerge('text-icon-default-primary', props.className);
    const filteredProps = omitFields(props as unknown as Record<string, unknown>, ['size', 'name', 'icon']);

    return (
        <Suspense fallback={<svg width={size ?? defaultSize} height={size ?? defaultSize} />}>
            <IconComponent {...filteredProps} width={size} height={size} className={iconClasses} />
        </Suspense>
    );
}
